// AddAsset1.js (Excel-style entry — no left bar, all categories load initially)
// npm i react-bootstrap axios
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Table,
  InputGroup,
  Badge,
  Spinner,
} from "react-bootstrap";
import axios from "axios";
import TopNavbar from "../../Components/Navbar/Navbar";
import "./AddAssest.css";
 
const uid = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;
 
const ASSET_TYPES = [
  "Desktop Computer","Laptop","Monitor","Keyboard","Mouse","Headphones / Earphones","Web Camera",
  "Laptop Charger","Computer UPS","Docking Station","External Hard Drive","Pen Drive","Laser Printer",
  "Inkjet Printer","All-in-One Printer/Scanner","Scanner","Barcode Scanner","Router","WiFi Access Point",
  "LAN Switch","LAN Cable","Network Rack","Biometric Attendance Machine","Mobile Phone","Tablet",
  "SIM Card (Postpaid)","SIM Card (Prepaid)","Power Bank","Hotspot Dongle","Bluetooth Device",
  "Bluetooth Headset","Mobile Charger","Chair","Table","Desk","Storage Cupboard","Almirah","Display Rack",
  "Notice Board","Whiteboard","Ceiling Fan","Table Fan","Pedestal Fan","Air Conditioner - Split",
  "Air Conditioner - Window","Refrigerator","Water Dispenser","Microwave Oven","Electric Kettle","Inverter",
  "Inverter Battery","Stabilizer","Extension Board","Spike Guard","LED Tube Light","LED Panel Light",
  "LED Bulb","Projector","Projector Screen","Laminator","Calculator","POS Machine","CCTV Camera","DVR/NVR",
  "LED TV","Speakers","Label Printer","Fire Extinguisher","First Aid Kit","Biometric Door Lock",
  "Tea/Coffee Machine","Water Bottle","Water Jug","Glass Set","Cup Set","Dustbin","Cleaning Kit","Umbrella",
  "Tool Kit","Ladder",
];
 
// build a blank row (optionally with a preset asset type)
const makeRow = (asset_type = "") => ({
  id: uid(),
  asset_type,
  quantity: 1,
  condition: "",
  purchase_year: new Date().getFullYear(),
  asset_value: "",
  remarks: "",
  device_user_name: "",
  serial_number: "",
  make: "",      
  model: "",     
});
 
// one row per asset type, in original order
const buildAllRows = () => ASSET_TYPES.map((t) => makeRow(t));
 
// a row counts toward submission only if the user actually filled something
const isRowFilled = (r) =>
  String(r.serial_number || "").trim() !== "" ||
  String(r.asset_value || "").trim() !== "" ||
  String(r.device_user_name || "").trim() !== "" ||
  String(r.make || "").trim() !== "" ||     
  String(r.model || "").trim() !== "";
 
const AddAsset1 = () => {
  const [userMeta, setUserMeta] = useState({ username: "", Areaoffice: "" });
 
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("user2");
      if (raw) {
        const u = JSON.parse(raw);
        setUserMeta({
          username: u?.username || "",
          Areaoffice: u?.Areaoffice || "",
        });
      }
    } catch (e) {
      console.error("Failed to parse session user2:", e);
    }
  }, []);
 
  // ✅ all categories present from the start
  const [rows, setRows] = useState(() => buildAllRows());
 
  const [search, setSearch] = useState("");
 
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [submitting, setSubmitting] = useState(false);
 
  const showNotification = (msg, type = "success") => {
    setAlertMessage(msg);
    setAlertType(type);
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 4500);
  };
 
  // filter rows by asset type for display only (editing still uses row id)
  const visibleRows = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => (r.asset_type || "").toLowerCase().includes(s));
  }, [search, rows]);
 
  const addBlankRow = () => setRows((prev) => [makeRow(""), ...prev]);
 
  const resetToAll = () => {
    setRows(buildAllRows());
    setSearch("");
    showNotification("Reloaded all asset categories.");
  };
 
  const clearAll = () => setRows([]);
 
  const removeRow = (id) => setRows((prev) => prev.filter((r) => r.id !== id));
 
  const duplicateRow = (id) =>
    setRows((prev) => {
      const found = prev.find((x) => x.id === id);
      if (!found) return prev;
      const copy = { ...found, id: uid() };
      const idx = prev.findIndex((x) => x.id === id);
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
 
  const updateRow = (id, key, value) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)));
 
  const filledRows = useMemo(() => rows.filter(isRowFilled), [rows]);
 
  const totals = useMemo(() => {
    const count = filledRows.length;
    const totalValue = filledRows.reduce((sum, r) => sum + (Number(r.asset_value) || 0), 0);
    return { count, totalValue };
  }, [filledRows]);
 
  const validate = () => {
    if (!userMeta.username) {
      showNotification("❌ Session username missing. Please login again.", "danger");
      return false;
    }
 
    if (filledRows.length === 0) {
      showNotification("❌ Please fill at least 1 asset (Serial No / Value / User).", "danger");
      return false;
    }
 
    for (let i = 0; i < filledRows.length; i++) {
      const r = filledRows[i];
      const label = r.asset_type || `Row ${i + 1}`;
 
      if (!r.asset_type)
        return showNotification(`❌ ${label}: Asset Type missing`, "danger"), false;
 
      const year = Number(r.purchase_year);
      const nowYear = new Date().getFullYear();
      if (!year || year < 2000 || year > nowYear)
        return showNotification(`❌ ${label}: Received Year invalid`, "danger"), false;
 
      if (r.asset_value === "" || Number(r.asset_value) < 0)
        return showNotification(`❌ ${label}: Asset Value invalid`, "danger"), false;
 
      if (!String(r.serial_number || "").trim())
        return showNotification(`❌ ${label}: Serial Number required`, "danger"), false;
    }
    return true;
  };
 
  const handleSubmitAll = async () => {
    if (!validate()) return;
 
    try {
      setSubmitting(true);
 
      const payload = {
        requestedBy: userMeta.username,
        Areaoffice: userMeta.Areaoffice,
        items: filledRows.map((r) => ({
          asset_type: r.asset_type,
          quantity: 1,
          condition: r.condition,
          purchase_year: Number(r.purchase_year),
          asset_value: Number(r.asset_value),
          remarks: r.remarks,
          device_user_name: r.device_user_name,
          serial_number: r.serial_number,
          make: r.make || "",     
    model: r.model || "",  
        })),
      };
 
      await axios.post("https://api.assetview.warrantyindia.com/api/assets/bulk", payload);
 
      showNotification(`✅ Submitted ${filledRows.length} asset(s) successfully!`);
      resetToAll();
    } catch (err) { 
      console.error(err);
      showNotification("❌ Bulk submit failed. Please try again.", "danger");
    } finally {
      setSubmitting(false);
    }
  };
 
  // shared asset-type <select> options
  const assetTypeOptions = (
<>
<option value="">Select asset type...</option>
      {ASSET_TYPES.map((t) => (
<option key={t} value={t}>
          {t}
</option>
      ))}
</>
  );
 
  return (
<>
<TopNavbar />
 
      <div className="addasset-page">
<Container fluid className="py-4">
<Row className="" style={{width:"110%"}}>
<Col xl={11} lg={12} md={12} sm={12}>
              {showAlert && (
<Alert
                  variant={alertType}
                  className="mb-3 alert-fade-in"
                  dismissible
                  onClose={() => setShowAlert(false)}
>
                  {alertMessage}
</Alert>
              )}
 
              <Card className="shadow-lg border-0">
<Card.Header className="py-3">
<div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2">
<div>
<h4 className="mb-1">Asset Bulk Entry</h4>

 
                      
</div>
 
                    <div className="d-flex flex-wrap gap-2">
<Button
                        type="button"
                        variant="outline-secondary"
                        onClick={resetToAll}
                        disabled={submitting}
>
                        Reset to All
</Button>
 
                      
 
                      <Button
                        type="button"
                        variant="primary"
                        onClick={handleSubmitAll}
                        disabled={totals.count === 0 || submitting}
>
                        {submitting ? (
<>
<Spinner size="sm" animation="border" className="me-2" />
                            Submitting...
</>
                        ) : (
<>Submit ({totals.count})</>
                        )}
</Button>
</div>
</div>
</Card.Header>
 
                <Card.Body>
                  {/* TOOLBAR */}
<div className="d-flex flex-column flex-md-row gap-2 mb-3 align-items-stretch align-items-md-center">
<InputGroup style={{ maxWidth: 360 }}>
<Form.Control
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search asset type..."
                        style={{height:"10px"}}
                      />

</InputGroup>
 
                    
 
                    
</div>
 
                  {rows.length === 0 ? (
<div className="text-muted">
                      No rows. Click <b>+ Add Row</b> or <b>Reset to All</b>.
</div>
                  ) : visibleRows.length === 0 ? (
<div className="text-muted">No asset type matches your search.</div>
                  ) : (
<>
                      {/* DESKTOP TABLE */}
<div className="d-none d-md-block" style={{ overflowX: "auto" }}>
<Table bordered hover responsive className="mb-0 align-middle">
<thead>
<tr>
<th style={{ width: 50 }}>#</th>
<th style={{ minWidth: 220 }}>Asset Type *</th>
<th style={{ minWidth: 150 }}>Serial No *</th>
<th style={{ minWidth: 150 }}>Make</th>        
<th style={{ minWidth: 150 }}>Model</th>      
<th style={{ minWidth: 160 }}>Device User Name</th>
<th style={{ minWidth: 140 }}>Condition</th>
<th style={{ width: 120 }}>Year *</th>
<th style={{ width: 140 }}>Value (₹) *</th>
<th style={{ minWidth: 200 }}>Remarks</th>
<th style={{ width: 120 }}>Action</th>
</tr>
</thead>
 
                          <tbody>
                            {visibleRows.map((r, idx) => {
                              const filled = isRowFilled(r);
                              return (
<tr key={r.id} className={filled ? "table-active" : ""}>
<td>{idx + 1}</td>
 
                                  <td>
<Form.Select
                                      value={r.asset_type}
                                      onChange={(e) =>
                                        updateRow(r.id, "asset_type", e.target.value)
                                      }
>
                                      {assetTypeOptions}
</Form.Select>
</td>
 
                                  <td>
<Form.Control
                                      value={r.serial_number}
                                      onChange={(e) =>
                                        updateRow(r.id, "serial_number", e.target.value)
                                      }
                                      placeholder="Serial Number"
                                    />
</td>
{/* ADD THESE TWO NEW COLUMNS */}
<td>
  <Form.Control
    value={r.make}
    onChange={(e) => updateRow(r.id, "make", e.target.value)}
    placeholder="Make"
  />
</td>
<td>
  <Form.Control
    value={r.model}
    onChange={(e) => updateRow(r.id, "model", e.target.value)}
    placeholder="Model"
  />
</td>
 
                                  <td>
<Form.Control
                                      value={r.device_user_name}
                                      onChange={(e) =>
                                        updateRow(r.id, "device_user_name", e.target.value)
                                      }
                                      placeholder="User Name"
                                    />
</td>
 
                                  <td>
<Form.Select
                                      value={r.condition}
                                      onChange={(e) =>
                                        updateRow(r.id, "condition", e.target.value)
                                      }
>
<option value="">Select</option>
<option value="Excellent">Excellent</option>
<option value="Good">Good</option>
<option value="Fair">Fair</option>
<option value="Poor">Poor</option>
<option value="Damaged">Damaged</option>
</Form.Select>
</td>
 
                                  <td>
<Form.Control
                                      type="number"
                                      min="2000"
                                      max={new Date().getFullYear()}
                                      value={r.purchase_year}
                                      onChange={(e) =>
                                        updateRow(r.id, "purchase_year", e.target.value)
                                      }
                                      style={{ width: "100px" }}
                                    />
</td>
 
                                  <td>
<Form.Control
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      value={r.asset_value}
                                      onChange={(e) =>
                                        updateRow(r.id, "asset_value", e.target.value)
                                      }
                                      placeholder="0"
                                      style={{ width: "110px" }}
                                    />
</td>
 
                                  <td>
<Form.Control
                                      value={r.remarks}
                                      onChange={(e) => updateRow(r.id, "remarks", e.target.value)}
                                      placeholder="Optional"
                                    />
</td>
 
                                  <td>
<div className="d-flex gap-2">
<Button
                                        type="button"
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => duplicateRow(r.id)}
                                        disabled={submitting}
                                        title="Duplicate row"
>
                                        +1
</Button>
 
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => removeRow(r.id)}
                                        disabled={submitting}
                                        title="Remove row"
>
                                        Del
</Button>
</div>
</td>
</tr>
                              );
                            })}
</tbody>
</Table>
</div>
 
                      {/* MOBILE CARDS */}
<div className="d-block d-md-none">
                        {visibleRows.map((r, idx) => (
<Card key={r.id} className="mb-3 shadow-sm">
<Card.Body>
<div className="d-flex justify-content-between gap-2 mb-2">
<div style={{ fontWeight: 700 }}>{idx + 1}.</div>
<div className="d-flex gap-2">
<Button
                                    type="button"
                                    size="sm"
                                    variant="outline-primary"
                                    onClick={() => duplicateRow(r.id)}
                                    disabled={submitting}
>
                                    +1
</Button>
<Button
                                    type="button"
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => removeRow(r.id)}
                                    disabled={submitting}
>
                                    Del
</Button>
</div>
</div>
 
                              <Form.Group className="mb-2">
<Form.Label className="mb-1">Asset Type *</Form.Label>
<Form.Select
                                  value={r.asset_type}
                                  onChange={(e) => updateRow(r.id, "asset_type", e.target.value)}
>
                                  {assetTypeOptions}
</Form.Select>
</Form.Group>
 
                              <Form.Group className="mb-2">
<Form.Label className="mb-1">Serial No *</Form.Label>
<Form.Control
                                  value={r.serial_number}
                                  onChange={(e) =>
                                    updateRow(r.id, "serial_number", e.target.value)
                                  }
                                  placeholder="Serial Number"
                                />
</Form.Group>

{/* ADD THESE TWO NEW FORM GROUPS */}
<Form.Group className="mb-2">
  <Form.Label className="mb-1">Make</Form.Label>
  <Form.Control
    value={r.make}
    onChange={(e) => updateRow(r.id, "make", e.target.value)}
    placeholder="Make"
  />
</Form.Group>

<Form.Group className="mb-2">
  <Form.Label className="mb-1">Model</Form.Label>
  <Form.Control
    value={r.model}
    onChange={(e) => updateRow(r.id, "model", e.target.value)}
    placeholder="Model"
  />
</Form.Group>
 
                              <Form.Group className="mb-2">
<Form.Label className="mb-1">Device User Name</Form.Label>
<Form.Control
                                  value={r.device_user_name}
                                  onChange={(e) =>
                                    updateRow(r.id, "device_user_name", e.target.value)
                                  }
                                  placeholder="User Name"
                                />
</Form.Group>
 
                              <Row className="g-2">
<Col xs={6}>
<Form.Group className="mb-2">
<Form.Label className="mb-1">Condition</Form.Label>
<Form.Select
                                      value={r.condition}
                                      onChange={(e) =>
                                        updateRow(r.id, "condition", e.target.value)
                                      }
>
<option value="">Select</option>
<option value="Excellent">Excellent</option>
<option value="Good">Good</option>
<option value="Fair">Fair</option>
<option value="Poor">Poor</option>
<option value="Damaged">Damaged</option>
</Form.Select>
</Form.Group>
</Col>
 
                                <Col xs={6}>
<Form.Group className="mb-2">
<Form.Label className="mb-1">Year *</Form.Label>
<Form.Control
                                      type="number"
                                      min="2000"
                                      max={new Date().getFullYear()}
                                      value={r.purchase_year}
                                      onChange={(e) =>
                                        updateRow(r.id, "purchase_year", e.target.value)
                                      }
                                    />
</Form.Group>
</Col>
</Row>
 
                              <Form.Group className="mb-2">
<Form.Label className="mb-1">Value (₹) *</Form.Label>
<Form.Control
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={r.asset_value}
                                  onChange={(e) =>
                                    updateRow(r.id, "asset_value", e.target.value)
                                  }
                                  placeholder="0"
                                />
</Form.Group>
 
                              <Form.Group className="mb-0">
<Form.Label className="mb-1">Remarks</Form.Label>
<Form.Control
                                  value={r.remarks}
                                  onChange={(e) => updateRow(r.id, "remarks", e.target.value)}
                                  placeholder="Optional"
                                />
</Form.Group>
</Card.Body>
</Card>
                        ))}
</div>
</>
                  )}
</Card.Body>
 
                <Card.Footer className="bg-white">
<div className="d-flex flex-column flex-md-row justify-content-end gap-2">
<Button
                      type="button"
                      variant="outline-secondary"
                      onClick={resetToAll}
                      disabled={submitting}
>
                      Reset to All
</Button>
<Button
                      type="button"
                      variant="primary"
                      onClick={handleSubmitAll}
                      disabled={totals.count === 0 || submitting}
>
                      {submitting ? (
<>
<Spinner size="sm" animation="border" className="me-2" />
                          Submitting...
</>
                      ) : (
<>Submit ({totals.count})</>
                      )}
</Button>
</div>
</Card.Footer>
</Card>
</Col>
</Row>
</Container>
</div>
</>
  );
};
 
export default AddAsset1;